import { Star, MessageSquareQuote, ExternalLink } from "lucide-react";
import { SITE } from "@/data/site";

// Reviews live on Google (owner preference). We never fabricate review text or
// ratings; on-site cards render only when real, verified reviews are supplied
// through the admin. Otherwise we point visitors to the live Google profile.
export default function ReviewsSection({ reviews = [], loading = false }) {
  return (
    <div data-testid="reviews-section">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <p className="text-neutral-400">
          {reviews.length > 0
            ? "What Kensmarc Electric customers are saying."
            : "Read real customer reviews for Kensmarc Electric on Google."}
        </p>
        <ReviewLink href={SITE.googleReviewUrl} label="Leave a Review" testId="leave-review-btn" />
      </div>

      {loading ? (
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-44 animate-pulse rounded-xl bg-[#15151c]" />
          ))}
        </div>
      ) : reviews.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-[#2a2a34] bg-[#101017] p-10 text-center sm:p-14" data-testid="reviews-empty">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-400/10">
            <MessageSquareQuote className="h-8 w-8 text-amber-400" />
          </div>
          <h3 className="mt-6 font-display text-2xl font-bold uppercase text-white">Reviews on Google</h3>
          <p className="mx-auto mt-3 max-w-lg text-neutral-400">
            Kensmarc Electric's customer reviews live on Google. Read what real customers have to say — or
            share your own experience.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <ReviewLink href={SITE.googleUrl} label="Read reviews on Google" testId="reviews-google-cta" />
            <ReviewLink href={SITE.googleReviewUrl} label="Write a review" testId="reviews-write-cta" />
          </div>
        </div>
      ) : (
        <>
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {reviews.map((r) => (
              <ReviewCard key={r.id} review={r} />
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <ReviewLink href={SITE.googleUrl} label="Read more reviews on Google" testId="reviews-google-cta" />
          </div>
        </>
      )}
    </div>
  );
}

function ReviewLink({ href, label, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="inline-flex items-center gap-2 rounded-full border border-[#2e2e39] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:border-amber-400/60"
    >
      <Star className="h-4 w-4 text-amber-400" /> {label}
      <ExternalLink className="h-3.5 w-3.5 text-neutral-400" />
    </a>
  );
}

function ReviewCard({ review }) {
  const hasRating = Number.isFinite(review.rating) && review.rating >= 1 && review.rating <= 5;
  return (
    <div className="kx-card flex flex-col p-6" data-testid={`review-card-${review.id?.slice(0, 6)}`}>
      {hasRating && <Stars value={review.rating} />}
      <p className={`${hasRating ? "mt-4" : ""} flex-1 leading-relaxed text-neutral-200`}>"{review.message}"</p>
      <div className="mt-5 border-t border-[#20202a] pt-4">
        <p className="font-semibold text-white">{review.name}</p>
        {(review.location || review.source || review.date) && (
          <p className="text-sm text-neutral-500">
            {[review.location, review.date, review.source].filter(Boolean).join(" · ")}
          </p>
        )}
      </div>
    </div>
  );
}

function Stars({ value = 0 }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`h-5 w-5 ${n <= value ? "fill-amber-400 text-amber-400" : "text-neutral-600"}`} />
      ))}
    </div>
  );
}
